import { useMemo } from "react";
import {
  CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis,
} from "recharts";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { CockpitPanel } from "@/cockpit/CockpitPanel";
import { FixedChart } from "@/cockpit/FixedChart";
import { MagnitudeBar } from "@/cockpit/FlowSplitBar";
import { AXIS, GRID, OUTFLOW, compactShares, compactUsd, fmtMd, tooltipBox } from "@/cockpit/formatCockpit";

const STEP_H = 132;

export function DilutionPressureLine({ sales, dataSourceStatus, onOpenRaw }) {
  const rows = useMemo(() => {
    const list = [...(sales || [])].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
    let cumul = 0;
    return list.map((s) => {
      cumul += Number(s.shares) || 0;
      return { ...s, cumulativeShares: cumul };
    });
  }, [sales]);
  const totalSh = rows.length ? rows[rows.length - 1].cumulativeShares : 0;
  const totalUsd = rows.reduce((s, r) => s + (Number(r.marketValueUsd) || 0), 0);
  const maxSh = Math.max(1, ...rows.map((r) => Number(r.shares) || 0));
  const fromMongo = dataSourceStatus === "fdn-mongo";
  const pending = dataSourceStatus === "pending-confirmation";

  return (
    <CockpitPanel
      title="Form 144 · proposed sales"
      hint="Proposed, not confirmed sold. Metric is cumulative proposed shares from the house-feed snapshot — ADV% is not available from this feed."
      badge={(
        <StatusBadge
          tier={pending ? "caution" : "info"}
          text={fromMongo ? "FDN Mongo" : pending ? "Pending data-source confirmation" : "Form 144"}
          title="Form 144 via FinancialData.net house feed when status is FDN Mongo."
          testid="dilution-source-badge"
        />
      )}
      onOpenRaw={onOpenRaw}
      testid="dilution-pressure-line"
    >
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-[18px] font-mono-data font-medium">{compactShares(totalSh)}</span>
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          cumulative proposed shares
        </span>
      </div>
      {rows.length > 0 ? (
        <>
          {rows.length > 1 ? (
            <FixedChart height={STEP_H}>
              {(width) => (
                <LineChart
                  width={width}
                  height={STEP_H}
                  data={rows}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  onClick={onOpenRaw}
                >
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: AXIS, fontSize: 9 }} tickFormatter={fmtMd} minTickGap={24} />
                  <YAxis tick={{ fill: AXIS, fontSize: 9 }} tickFormatter={compactShares} width={44} />
                  <Tooltip
                    contentStyle={tooltipBox}
                    formatter={(v) => [compactShares(v), "Cumulative proposed shares"]}
                    labelFormatter={fmtMd}
                  />
                  <Line
                    type="stepAfter"
                    dataKey="cumulativeShares"
                    stroke={OUTFLOW}
                    dot={false}
                    strokeWidth={1.7}
                    isAnimationActive={false}
                  />
                </LineChart>
              )}
            </FixedChart>
          ) : null}
          <div
            className="h-2.5 rounded-sm overflow-hidden flex my-3"
            style={{ background: "var(--hoip-hover)" }}
            data-testid="form144-composition-gauge"
          >
            {rows.map((s) => (
              <div
                key={s.id}
                style={{
                  width: `${totalSh > 0 ? ((Number(s.shares) || 0) / totalSh) * 100 : 0}%`,
                  background: OUTFLOW,
                  opacity: 0.45 + (0.55 * ((Number(s.shares) || 0) / maxSh)),
                  height: "100%",
                  borderRight: "1px solid var(--hoip-bg)",
                }}
              />
            ))}
          </div>
          <div className="space-y-1.5" data-testid="form144-sales-list">
            <div className="flex items-center text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
              <span className="w-[44%]">Seller</span>
              <span className="w-[22%] text-right">Shares</span>
              <span className="flex-1 pl-2">Size vs largest notice</span>
            </div>
            {rows.map((s) => (
              <div key={s.id} className="flex items-center gap-2 text-[10px]">
                <span className="w-[44%] truncate" title={`${s.date} ${s.seller}`}>{s.date} {s.seller}</span>
                <span className="w-[22%] text-right font-mono-data">{compactShares(s.shares)}</span>
                <MagnitudeBar value={s.shares} max={maxSh} color={OUTFLOW} />
              </div>
            ))}
          </div>
          {totalUsd > 0 ? (
            <div className="text-[10px] mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
              Disclosed market value {compactUsd(totalUsd)}
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No Form 144 notices in this snapshot.</p>
      )}
    </CockpitPanel>
  );
}
